// src/cockpit4/components/KnowledgeQA.tsx
// ============================================
// P0-功能1：智能知识问答（知识中心）
//
// 功能：
//   1. 聊天式界面（消息气泡：用户问 / AI 答）
//   2. 自然语言提问输入框
//   3. AI 检索 + 生成答案（调用 RuntimeManager.query 获取相关知识，基于检索结果生成答案）
//   4. 答案带引用来源卡片（可点击查看原文，通过 /vault-api/read 读取）
//   5. 找不到答案时自动标记为知识缺口（显示"知识库里没有找到相关记录"）
//   6. 相关问题推荐（基于当前问题推荐 3 个相关问题）
//   7. 多轮对话支持（消息历史）
//
// 数据来源：
//   - RuntimeName.Knowledge 的 Search 查询（关键词检索）
//   - /vault-api/read 读取原文
//
// Hermes 铁律：
//   RAG 无命中时必须返回"知识库里没有找到相关记录"，
//   禁止 LLM 在无知识库依据时编造答案。
// ============================================

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Brain,
  Send,
  Sparkles,
  Loader2,
  RefreshCw,
  FileText,
  ExternalLink,
  AlertCircle,
  MessageSquareCode,
  X,
  Lightbulb,
  ChevronRight,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { KnowledgeQuery } from "../../runtime/KnowledgeRuntime/types";

// ============================================
// 类型定义
// ============================================

/** 单条引用来源 */
interface CitationSource {
  /** 引用序号（从 1 开始） */
  index: number;
  /** 标题 */
  title: string;
  /** Vault 相对路径 */
  path: string;
  /** 摘要片段（与问题最相关的段落） */
  snippet: string;
  /** 相关度评分（0-1） */
  score?: number;
}

/** 聊天消息 */
interface ChatMessage {
  /** 唯一 ID */
  id: string;
  /** 消息角色：用户 / AI */
  role: "user" | "assistant";
  /** 消息内容 */
  content: string;
  /** 时间戳 */
  timestamp: string;
  /** AI 回答的引用来源（仅 assistant 消息） */
  citations?: CitationSource[];
  /** 是否为知识缺口（RAG 无命中） */
  isKnowledgeGap?: boolean;
  /** 相关问题推荐（仅 assistant 最后一条消息） */
  relatedQuestions?: string[];
  /** 是否正在生成中（仅 assistant 消息） */
  loading?: boolean;
}

// ============================================
// 主组件
// ============================================

export default function KnowledgeQA() {
  // 消息历史（多轮对话）
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // 输入框内容
  const [input, setInput] = useState("");
  // 是否正在检索/生成
  const [thinking, setThinking] = useState(false);
  // 选中的引用原文（用于 Modal 展示）
  const [selectedSource, setSelectedSource] = useState<{
    title: string;
    path: string;
    content: string;
    loading: boolean;
  } | null>(null);
  // 消息列表容器引用（用于自动滚动到底部）
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ============================================
  // 自动滚动到底部
  // ============================================
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  // ============================================
  // 等待 RuntimeManager 就绪
  // ============================================
  const waitForRuntime = useCallback(
    (): Promise<boolean> => RuntimeManager.waitForRuntime({ timeoutMs: 6000, intervalMs: 300 }),
    []
  );

  // ============================================
  // 调用 Knowledge Search 检索相关知识
  // ============================================
  const searchKnowledge = useCallback(
    async (keyword: string, limit: number = 5): Promise<any[]> => {
      const ready = await waitForRuntime();
      if (!ready) return [];

      try {
        const resp = await RuntimeManager.query(RuntimeName.Knowledge, {
          type: KnowledgeQuery.Search,
          options: { keyword, limit },
        });
        if (!resp?.success || !resp.data) return [];

        // 解包嵌套结构：RuntimeManager 包了一层，KnowledgeRuntime 内部又包了一层
        const inner = resp.data as { success?: boolean; data?: any };
        if (inner?.success && inner.data) {
          const results = Array.isArray(inner.data)
            ? inner.data
            : inner.data.results || inner.data.items || [];
          return results;
        }
        // 兼容直接返回数组的情况
        if (Array.isArray(resp.data)) return resp.data;
        return [];
      } catch (err) {
        console.warn("[KnowledgeQA] 检索失败:", err);
        return [];
      }
    },
    [waitForRuntime]
  );

  // ============================================
  // 从检索结果中提取与问题最相关的片段
  // ============================================
  const extractRelevantSnippet = useCallback(
    (content: string, question: string, maxLen: number = 200): string => {
      if (!content) return "";
      // 提取问题中的关键词（长度 >= 2 的中文/英文词）
      const keywords = question
        .split(/[\s,，。？?！!、的了吗呢吧啊把被让使向到给对和与及或者而且是有什么如何为什么怎么怎样哪些哪个能否可以需要应该将会已经]/)
        .map((s) => s.trim())
        .filter((s) => s.length >= 2);

      // 按段落切分
      const paragraphs = content.split(/\n\n+/).filter((p) => p.trim().length > 10);
      if (paragraphs.length === 0) {
        return content.slice(0, maxLen);
      }

      // 找到包含最多关键词的段落
      let bestPara = paragraphs[0];
      let bestScore = -1;
      for (const para of paragraphs) {
        let score = 0;
        const lowerPara = para.toLowerCase();
        for (const kw of keywords) {
          if (lowerPara.includes(kw.toLowerCase())) {
            score += kw.length;
          }
        }
        // 优先选择长度适中的段落（避免过短或过长）
        if (para.length >= 30 && para.length <= 500) score += 5;
        if (score > bestScore) {
          bestScore = score;
          bestPara = para;
        }
      }

      // 截断到最大长度
      if (bestPara.length > maxLen) {
        return bestPara.slice(0, maxLen) + "...";
      }
      return bestPara;
    },
    []
  );

  // ============================================
  // 基于检索结果生成答案（抽取式，禁止编造）
  // ============================================
  const generateAnswer = useCallback(
    (question: string, results: any[]): {
      answer: string;
      citations: CitationSource[];
      isKnowledgeGap: boolean;
    } => {
      // Hermes 铁律：无命中时标记为知识缺口，禁止编造
      if (!results || results.length === 0) {
        return {
          answer: "知识库里没有找到相关记录。\n\n这可能是一个知识缺口，建议补充相关知识笔记后再提问。",
          citations: [],
          isKnowledgeGap: true,
        };
      }

      // 构建引用来源
      const citations: CitationSource[] = results.slice(0, 5).map((r, idx) => {
        const title = r.title || r.name || r.fileName || "未命名笔记";
        const path = r.path || r.filePath || r.folderName || "";
        const content = r.content || r.summary || "";
        const snippet = extractRelevantSnippet(content, question);
        return {
          index: idx + 1,
          title,
          path,
          snippet: snippet || (r.summary || "").slice(0, 200),
          score: r.score,
        };
      });

      // 抽取式生成答案：将各来源的相关片段拼接，标注引用序号
      const validCitations = citations.filter((c) => c.snippet);
      if (validCitations.length === 0) {
        // 有命中但无法提取有效片段
        const titles = citations.map((c) => `「${c.title}」`).join("、");
        return {
          answer: `根据知识库检索，找到以下相关记录：${titles}。\n\n但未能提取到具体内容片段，建议点击下方引用来源查看原文。`,
          citations,
          isKnowledgeGap: false,
        };
      }

      // 拼接各片段为答案，每个片段标注引用序号
      const parts: string[] = [];
      parts.push(`根据知识库检索到 ${results.length} 条相关记录，整理如下：`);
      parts.push("");
      for (const c of validCitations) {
        parts.push(`[${c.index}] ${c.snippet}`);
        parts.push("");
      }
      parts.push("以上内容均来自知识库原文，点击下方引用卡片可查看完整原文。");

      return {
        answer: parts.join("\n"),
        citations,
        isKnowledgeGap: false,
      };
    },
    [extractRelevantSnippet]
  );

  // ============================================
  // 生成相关问题推荐（基于当前问题和检索结果）
  // ============================================
  const generateRelatedQuestions = useCallback(
    (question: string, results: any[]): string[] => {
      const related: string[] = [];

      // 策略 1：从其他检索结果标题衍生问题
      for (const r of results) {
        if (related.length >= 3) break;
        const title = r.title || r.name || r.fileName;
        if (title && !title.includes(question)) {
          related.push(`${title} 的详细内容是什么？`);
        }
      }

      // 策略 2：基于问题关键词生成追问
      if (related.length < 3) {
        const lowerQ = question.toLowerCase();
        if (lowerQ.includes("什么") || lowerQ.includes("什么是")) {
          // "什么是 X" → 追问 X 的应用/案例
          const subject = question.replace(/什么是|什么/g, "").replace(/？|\?/g, "").trim();
          if (subject) {
            related.push(`${subject} 有哪些实际应用案例？`);
            related.push(`${subject} 的核心原理是什么？`);
          }
        } else if (lowerQ.includes("如何") || lowerQ.includes("怎么")) {
          related.push("有哪些最佳实践和注意事项？");
          related.push("相关工具或资源有哪些？");
        } else if (lowerQ.includes("为什么")) {
          related.push("有什么具体的例子可以说明？");
          related.push("如何应对或解决这个问题？");
        } else {
          // 通用追问模板
          related.push("这个主题的核心概念是什么？");
          related.push("有哪些相关的实践案例？");
        }
      }

      // 去重并截取 3 个
      const unique = Array.from(new Set(related));
      return unique.slice(0, 3);
    },
    []
  );

  // ============================================
  // 发送问题
  // ============================================
  const sendQuestion = useCallback(
    async (question: string) => {
      const trimmed = question.trim();
      if (!trimmed || thinking) return;

      // 添加用户消息
      const userMsg: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: trimmed,
        timestamp: new Date().toISOString(),
      };
      // 添加 AI 占位消息（loading 状态）
      const aiMsgId = `ai-${Date.now()}`;
      const aiPlaceholder: ChatMessage = {
        id: aiMsgId,
        role: "assistant",
        content: "",
        timestamp: new Date().toISOString(),
        loading: true,
      };
      setMessages((prev) => [...prev, userMsg, aiPlaceholder]);
      setInput("");
      setThinking(true);

      try {
        // 1. 调用 Knowledge Search 检索
        const results = await searchKnowledge(trimmed, 5);

        // 2. 基于检索结果生成答案（抽取式）
        const { answer, citations, isKnowledgeGap } = generateAnswer(trimmed, results);

        // 3. 生成相关问题推荐
        const relatedQuestions = generateRelatedQuestions(trimmed, results);

        // 4. 更新 AI 消息
        setMessages((prev) =>
          prev.map((m) =>
            m.id === aiMsgId
              ? {
                  ...m,
                  content: answer,
                  citations,
                  isKnowledgeGap,
                  relatedQuestions,
                  loading: false,
                }
              : m
          )
        );
      } catch (err) {
        // 检索过程异常
        setMessages((prev) =>
          prev.map((m) =>
            m.id === aiMsgId
              ? {
                  ...m,
                  content: `检索过程中出现错误：${(err as Error).message}`,
                  loading: false,
                }
              : m
          )
        );
      } finally {
        setThinking(false);
      }
    },
    [thinking, searchKnowledge, generateAnswer, generateRelatedQuestions]
  );

  // ============================================
  // 读取引用原文（点击引用卡片）
  // ============================================
  const openSource = useCallback(async (citation: CitationSource) => {
    setSelectedSource({
      title: citation.title,
      path: citation.path,
      content: "",
      loading: true,
    });
    try {
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(citation.path)}`);
      if (resp.ok) {
        const json = await resp.json();
        if (json?.success && json.content) {
          setSelectedSource({
            title: citation.title,
            path: citation.path,
            content: json.content,
            loading: false,
          });
          return;
        }
      }
      setSelectedSource({
        title: citation.title,
        path: citation.path,
        content: "读取失败：文件不存在或无访问权限",
        loading: false,
      });
    } catch (err) {
      setSelectedSource({
        title: citation.title,
        path: citation.path,
        content: `读取失败：${(err as Error).message}`,
        loading: false,
      });
    }
  }, []);

  // ============================================
  // 清空对话
  // ============================================
  const clearConversation = useCallback(() => {
    setMessages([]);
    setInput("");
  }, []);

  // ============================================
  // 推荐的初始问题（无消息时显示）
  // ============================================
  const suggestedQuestions = useMemo(
    () => [
      "XEAOS 企业操作系统的核心架构是什么？",
      "如何使用知识中心的 RAG 检索功能？",
      "Hermes 运行时的主要能力有哪些？",
      "Obsidian Vault 的目录结构是如何组织的？",
    ],
    []
  );

  // 是否有空状态（无消息）
  const isEmpty = messages.length === 0;

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative h-full flex flex-col rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-violet-500/15 border border-violet-500/30">
            <Brain size={13} className="text-violet-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">智能知识问答</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Knowledge QA · RAG 检索 · 基于知识库生成答案
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-md border border-violet-500/20 bg-violet-500/5 text-[9px] font-mono text-violet-400 uppercase tracking-wider">
            <Sparkles size={9} />
            Hermes RAG
          </span>
          <button
            onClick={clearConversation}
            disabled={isEmpty || thinking}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-30"
            title="清空对话"
          >
            <RefreshCw size={11} className="text-gray-400" />
          </button>
        </div>
      </div>

      {/* 消息列表区域 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {isEmpty ? (
          // 空状态：欢迎 + 推荐问题
          <div className="h-full flex flex-col items-center justify-center text-center py-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="w-14 h-14 rounded-xl flex items-center justify-center bg-violet-500/10 border border-violet-500/20 mb-3"
            >
              <Brain size={24} className="text-violet-400/70" />
            </motion.div>
            <h4 className="text-[14px] font-semibold text-gray-300 mb-1">向知识库提问</h4>
            <p className="text-[11px] font-mono text-gray-500 max-w-md mb-4">
              基于企业知识库的 RAG 问答，答案仅来自检索到的真实记录，无命中时将标记为知识缺口。
            </p>
            <div className="grid grid-cols-2 gap-2 max-w-2xl w-full">
              {suggestedQuestions.map((q, idx) => (
                <button
                  key={idx}
                  onClick={() => sendQuestion(q)}
                  className="text-left p-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] hover:bg-violet-500/[0.06] hover:border-violet-500/30 transition-colors group"
                >
                  <div className="flex items-start gap-1.5">
                    <Lightbulb size={11} className="text-amber-400/70 flex-shrink-0 mt-0.5" />
                    <span className="text-[11px] text-gray-400 group-hover:text-violet-300 leading-snug">
                      {q}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          // 消息气泡列表
          messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              onOpenSource={openSource}
              onRelatedQuestionClick={sendQuestion}
              thinking={thinking}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* 输入框 */}
      <div className="border-t border-white/[0.04] p-3">
        <div className="flex items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 focus-within:border-violet-500/40 transition-colors">
          <MessageSquareCode size={13} className="text-violet-400/70 flex-shrink-0" />
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void sendQuestion(input);
              }
            }}
            placeholder="输入你的问题，按 Enter 发送..."
            disabled={thinking}
            className="flex-1 bg-transparent text-[12px] text-gray-200 placeholder-gray-600 outline-none font-mono"
          />
          <button
            onClick={() => void sendQuestion(input)}
            disabled={!input.trim() || thinking}
            className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-violet-500/15 border border-violet-500/30 hover:bg-violet-500/25 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {thinking ? (
              <Loader2 size={11} className="text-violet-400 animate-spin" />
            ) : (
              <Send size={11} className="text-violet-400" />
            )}
            <span className="text-[9px] font-mono text-violet-300 uppercase tracking-wider">
              {thinking ? "检索中" : "发送"}
            </span>
          </button>
        </div>
        <div className="mt-1.5 flex items-center justify-between px-1">
          <span className="text-[8px] font-mono text-gray-600">
            数据来源：Knowledge Runtime · Search 查询 · 答案仅来自知识库检索结果
          </span>
          {messages.length > 0 && (
            <span className="text-[8px] font-mono text-gray-700">
              {messages.filter((m) => m.role === "user").length} 轮对话
            </span>
          )}
        </div>
      </div>

      {/* 引用原文 Modal */}
      <AnimatePresence>
        {selectedSource && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-8"
            onClick={() => setSelectedSource(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-3xl max-h-[85vh] flex flex-col rounded-xl border border-violet-500/30 bg-[#0a0e1a] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={14} className="text-violet-400 flex-shrink-0" />
                  <h3 className="text-[14px] font-semibold text-white truncate">
                    {selectedSource.title}
                  </h3>
                </div>
                <button
                  onClick={() => setSelectedSource(null)}
                  className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors flex-shrink-0"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-5">
                {selectedSource.loading ? (
                  <div className="flex items-center justify-center py-12 text-gray-500">
                    <Loader2 size={16} className="animate-spin mr-2" />
                    <span className="text-[13px] font-mono">正在从 Vault 加载...</span>
                  </div>
                ) : (
                  <pre className="text-[13px] text-gray-300 whitespace-pre-wrap break-words font-mono leading-relaxed">
                    {selectedSource.content || "暂无内容"}
                  </pre>
                )}
              </div>
              <div className="flex items-center justify-between px-5 py-2 border-t border-white/[0.06] bg-white/[0.01]">
                <span className="text-[10px] font-mono text-gray-600 truncate" title={selectedSource.path}>
                  📁 {selectedSource.path}
                </span>
                <span className="text-[10px] font-mono text-gray-600">数据来源：Obsidian Vault</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 子组件：消息气泡
// ============================================

interface MessageBubbleProps {
  message: ChatMessage;
  onOpenSource: (citation: CitationSource) => void;
  onRelatedQuestionClick: (question: string) => void;
  thinking: boolean;
}

function MessageBubble({
  message,
  onOpenSource,
  onRelatedQuestionClick,
  thinking,
}: MessageBubbleProps) {
  const isUser = message.role === "user";

  // 用户消息：右对齐
  if (isUser) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.2 }}
        className="flex justify-end"
      >
        <div className="max-w-[75%] rounded-lg rounded-tr-sm border border-violet-500/30 bg-violet-500/10 px-3 py-2">
          <p className="text-[12px] text-gray-200 whitespace-pre-wrap break-words leading-relaxed">
            {message.content}
          </p>
          <span className="text-[8px] font-mono text-gray-600 mt-1 block text-right">
            {new Date(message.timestamp).toLocaleTimeString("zh-CN")}
          </span>
        </div>
      </motion.div>
    );
  }

  // AI 消息：左对齐
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2 }}
      className="flex justify-start"
    >
      <div className="max-w-[85%] w-full">
        <div className="flex items-center gap-1.5 mb-1">
          <div className="w-5 h-5 rounded flex items-center justify-center bg-violet-500/15 border border-violet-500/30">
            <Brain size={10} className="text-violet-400" />
          </div>
          <span className="text-[9px] font-mono text-violet-400 uppercase tracking-wider">
            AI 回答
          </span>
          {message.isKnowledgeGap && (
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono uppercase bg-amber-500/15 text-amber-400 border border-amber-500/30">
              <AlertCircle size={8} />
              知识缺口
            </span>
          )}
        </div>
        <div
          className={`rounded-lg rounded-tl-sm border px-3 py-2 ${
            message.isKnowledgeGap
              ? "border-amber-500/30 bg-amber-500/[0.04]"
              : "border-white/[0.06] bg-white/[0.02]"
          }`}
        >
          {message.loading ? (
            // 加载中状态
            <div className="flex items-center gap-2 py-1">
              <Loader2 size={12} className="text-violet-400 animate-spin" />
              <span className="text-[11px] font-mono text-gray-500">
                正在检索知识库...
              </span>
            </div>
          ) : (
            <>
              <p className="text-[12px] text-gray-300 whitespace-pre-wrap break-words leading-relaxed">
                {message.content}
              </p>
              <span className="text-[8px] font-mono text-gray-600 mt-1.5 block">
                {new Date(message.timestamp).toLocaleTimeString("zh-CN")}
              </span>
            </>
          )}
        </div>

        {/* 引用来源卡片 */}
        {!message.loading && message.citations && message.citations.length > 0 && (
          <div className="mt-2 space-y-1">
            <div className="flex items-center gap-1 px-1">
              <FileText size={9} className="text-gray-500" />
              <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                引用来源（{message.citations.length}）
              </span>
            </div>
            <div className="grid grid-cols-1 gap-1">
              {message.citations.map((c) => (
                <button
                  key={c.index}
                  onClick={() => onOpenSource(c)}
                  className="text-left p-2 rounded-md border border-white/[0.04] bg-white/[0.01] hover:bg-violet-500/[0.04] hover:border-violet-500/30 transition-colors group"
                >
                  <div className="flex items-start gap-1.5">
                    <span className="flex-shrink-0 w-4 h-4 rounded flex items-center justify-center bg-violet-500/15 text-violet-400 text-[9px] font-mono font-bold">
                      {c.index}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 mb-0.5">
                        <span className="text-[11px] text-gray-200 truncate group-hover:text-violet-300">
                          {c.title}
                        </span>
                        <ExternalLink size={9} className="text-gray-600 group-hover:text-violet-400 flex-shrink-0" />
                      </div>
                      {c.snippet && (
                        <p className="text-[10px] text-gray-500 line-clamp-2 leading-snug">
                          {c.snippet}
                        </p>
                      )}
                      {c.path && (
                        <p className="text-[8px] font-mono text-gray-700 mt-0.5 truncate" title={c.path}>
                          {c.path}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 相关问题推荐 */}
        {!message.loading && message.relatedQuestions && message.relatedQuestions.length > 0 && !thinking && (
          <div className="mt-2">
            <div className="flex items-center gap-1 px-1 mb-1">
              <Sparkles size={9} className="text-amber-400/70" />
              <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                相关问题
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {message.relatedQuestions.map((q, idx) => (
                <button
                  key={idx}
                  onClick={() => onRelatedQuestionClick(q)}
                  className="flex items-center gap-1 px-2 py-1 rounded-md border border-amber-500/20 bg-amber-500/[0.04] hover:bg-amber-500/[0.1] hover:border-amber-500/40 transition-colors group"
                >
                  <ChevronRight size={9} className="text-amber-400/60 group-hover:text-amber-400" />
                  <span className="text-[10px] text-gray-400 group-hover:text-amber-300">
                    {q}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
